import { differenceInCalendarDays } from 'date-fns';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { eventBus } from '@/events/eventBus';
import { forfeitDeposit } from '@/domain/depositFlow';
import { onFileExpiryIso } from '@/domain/confirmationFlow';
import { depositPaidAud } from '@/utils/bookingSummary';

// ---------------------------------------------------------------------------
// Ops cancel (vault System Diagram 09). Ops is the only role that can cancel a
// booking (enforced at the call site). A cancel always carries a typed reason,
// frees the slot so the Sweeper can rebook it, and applies the deposit fork:
// inside 7 days the deposit forfeits to the house, outside 7 days it is kept on
// file. Both outcomes route to the Sweeper. Mutations follow the *Flow.ts
// convention: read store, mutate via store actions, log, publish. "Now" is the
// sim clock, never Date.now().
// ---------------------------------------------------------------------------

function nowIso(): string {
  return useSimulationStore.getState().clockIso;
}

/**
 * The cancel deposit-fork window, in whole days. Grounded in the confirmation +
 * missed-top-up cancels (vault Diagram 06/09): a cancel with fewer than 7 whole
 * calendar days to the appointment forfeits the deposit; outside 7 days it is
 * kept on file.
 */
export const OPS_CANCEL_WINDOW_DAYS = 7;

/**
 * True when `startIso` is inside the forfeit window from `atIso`. Reuses the SAME
 * whole-calendar-day comparison the confirmation cancel (confirmationFlow) and the
 * missed-top-up cancel (topupChaseFlow `topupSevenDaySplit`) use, so every cancel
 * path splits the deposit on one shared boundary.
 */
export function isInsideCancelWindow(startIso: string, atIso: string): boolean {
  return differenceInCalendarDays(new Date(startIso), new Date(atIso)) < OPS_CANCEL_WINDOW_DAYS;
}

export type OpsCancelDepositOutcome = 'forfeited' | 'kept-on-file' | 'none';

export interface OpsCancelInput {
  bookingId: string;
  /** Mandatory typed reason - Ops cancels are always accountable. */
  reason: string;
  /** Persona / person cancelling (Ops). */
  by: string;
  at?: string;
}

export interface OpsCancelResult {
  ok: boolean;
  bookingId?: string;
  depositOutcome?: OpsCancelDepositOutcome;
  /** Present when a paid deposit was kept on file (outside 7 days): the surfaced deposit id. */
  surfacedDepositId?: string;
  error?: 'no-booking' | 'missing-reason' | 'already-cancelled';
}

/**
 * Ops cancels a booking. A typed reason is mandatory (a blank reason is refused
 * with no mutation). The slot is freed (booking -> cancelled) so the Sweeper can
 * rebook it, and a paid deposit is forked on the 7-day line:
 *  - inside 7 days  -> forfeit to the house (reuses `forfeitDeposit`; nets the
 *                      paid balance to zero, keeps the deposit linked as lapsed).
 *  - outside 7 days -> kept on file: unlinked from the booking + stamped with the
 *                      12-month on-file expiry, so the existing deposit-on-file
 *                      minting resurfaces it in the money-cohort / Sweeper pool.
 * Idempotent: a booking already cancelled is a no-op.
 */
export function opsCancelBooking(input: OpsCancelInput): OpsCancelResult {
  const brain = useBrainStore.getState();
  const reason = input.reason.trim();
  if (!reason) return { ok: false, error: 'missing-reason' };
  const booking = brain.bookings[input.bookingId];
  if (!booking) return { ok: false, error: 'no-booking' };
  if (booking.status === 'cancelled') return { ok: false, error: 'already-cancelled' };

  const at = input.at ?? nowIso();
  const deposit = booking.depositId ? brain.deposits[booking.depositId] : undefined;
  const hasPaidDeposit = !!deposit && deposit.status === 'paid' && depositPaidAud(deposit) > 0;

  let depositOutcome: OpsCancelDepositOutcome = 'none';
  let surfacedDepositId: string | undefined;

  if (hasPaidDeposit && deposit) {
    if (isInsideCancelWindow(booking.start, at)) {
      // INSIDE 7 days: the deposit forfeits to the house. This is a cancel, not a
      // no-show, so the booking is set `cancelled` below (never flipped to no-show).
      forfeitDeposit(booking.id, { reason: `Ops cancel: ${reason}`, by: input.by, at });
      depositOutcome = 'forfeited';
    } else {
      // OUTSIDE 7 days: keep the deposit on file. Unlink it from the booking so the
      // deposit-on-file minting resurfaces it in the money-cohort / Sweeper pool,
      // and stamp the 12-month on-file validity cap (same as the confirmation +
      // top-up outside-7d releases).
      brain.upsertDeposit({ ...deposit, bookingId: undefined, expiresAt: onFileExpiryIso(at) });
      depositOutcome = 'kept-on-file';
      surfacedDepositId = deposit.id;
    }
  }

  // Free the slot -> Sweeper (cancels & rebooks all go there).
  brain.upsertBooking({ ...booking, status: 'cancelled' });

  brain.appendActivity({
    id: `act-ops-cancel-${booking.id}`,
    type: 'booking-cancelled',
    customerId: booking.customerId,
    studioId: booking.studioId,
    artistId: booking.artistId,
    body: `Ops cancelled the booking - "${reason}". ${
      depositOutcome === 'forfeited'
        ? 'Deposit forfeited to the house (inside 7 days).'
        : depositOutcome === 'kept-on-file'
          ? 'Deposit kept on file (outside 7 days).'
          : 'No deposit on file.'
    } Slot freed to the Sweeper.`,
    timestamp: at,
  });

  eventBus.publish('ops:booking-cancelled', {
    bookingId: booking.id,
    customerId: booking.customerId,
    studioId: booking.studioId,
    depositOutcome,
    surfacedDepositId,
    at,
  });

  return { ok: true, bookingId: booking.id, depositOutcome, surfacedDepositId };
}
